/**
 * AppV2 — minimal React shell.
 *
 * All business logic lives in app-runtime.ts (pure TS, zero React imports).
 * This component only: creates the runtime, subscribes to state changes,
 * and renders the element tree via RenderContext.
 *
 * Block-Centric Lifecycle:
 * - Runtime is created ONCE via useRef (not useMemo) — survives blocks changes.
 * - blocks prop changes → addBlocks() — only new blocks are processed, existing state preserved.
 * - Generation counter protects against StrictMode double-mount race conditions.
 */

import React, { useEffect, useMemo, useRef, useState } from "react"
import { RenderContext, BreakpointProvider, getIsolatedAppStyle } from "@cogo/react-renderer"
import type { RenderContextValue, CogoBlockDef } from "@cogo/react-renderer"
import type { App, BlockDef } from "@cogo/types-v2"
import type { PlatformAdapter, ToastHandler } from "@cogo/runtime-v2"
import { StateManager, ActionFlowEngine } from "@cogo/runtime-v2"
import type { ResolvedTheme } from "@cogo/design-presets"
import { createAppRuntime, getSharedRegistry } from "../runtime/app-runtime"
import type { AppRuntime, ThemeTokens } from "../runtime/app-runtime"
import type { AppSandbox } from "../sandbox/app-sandbox.types"
import { AtomDefaultsProvider } from "../context/atom-defaults-context"
import { CogoAgent } from "./CogoAgent"
import { LayoutWrapper, type Layout } from "./layouts"

export interface AppV2Props {
  manifest: App
  blocks: Record<string, BlockDef>
  platform?: PlatformAdapter
  /** AppSandbox instance — attaches sandbox modules to PlatformAdapter for runtime isolation */
  sandbox?: AppSandbox | null
  onToast?: ToastHandler
  initialRoute?: string
  onNavigate?: (route: string) => void | Promise<void>
  onReady?: (ctx: { stateManager: StateManager; actionEngine: ActionFlowEngine }) => void
  designSystem?: Record<string, string>
  /** Theme tokens (colors + radius) — injected into :root CSS variables */
  themeTokens?: ThemeTokens
  /** Resolved design-system theme — cssVars merged on top of themeTokens; atomProps feed AtomDefaultsProvider */
  theme?: ResolvedTheme
  /** Optional agentic page spec — wraps rendered blocks in a layout (stack/2col/grid) */
  pageSpec?: { blocks: string[]; layout: Layout; context: Record<string, unknown> }
}

export { getSharedRegistry }

export function AppV2({ manifest, blocks, platform, sandbox, onToast, initialRoute, onNavigate, onReady, designSystem, themeTokens, theme, pageSpec }: AppV2Props) {
  const [tick, forceUpdate] = useState(0)
  const runtimeRef = useRef<AppRuntime | null>(null)
  const initFlowsRanRef = useRef(false)
  const appRootRef = useRef<HTMLDivElement>(null)

  // Compose platform adapter with sandbox modules (if provided)
  const effectivePlatform = useMemo(() => {
    if (!sandbox) return platform
    return { ...platform, sandbox: sandbox.modules } as PlatformAdapter
  }, [platform, sandbox])

  // Create runtime ONCE — never recreated on blocks changes.
  // If manifest changes (different app), we'd need a full remount via key prop.
  if (!runtimeRef.current) {
    runtimeRef.current = createAppRuntime(manifest, blocks, { platform: effectivePlatform, onToast, initialRoute, onNavigate, designSystem, themeTokens })
  }
  const runtime = runtimeRef.current

  // When blocks change (background loading), add only new blocks — runtime is NOT recreated.
  // Existing state (including async fetch results) is preserved.
  useEffect(() => {
    const added = runtime.addBlocks(blocks)
    if (added.length > 0) {
      forceUpdate((n) => n + 1)
      // First block batch arrived after mount with empty blocks (full page reload):
      // run init flows with isRouteChange=true to apply state defaults.
      if (!initFlowsRanRef.current) {
        initFlowsRanRef.current = true
        runtime.runInitFlows()
      }
    }
  }, [blocks, runtime])

  // Subscribe to state changes → re-render
  useEffect(() => runtime.subscribe(() => forceUpdate((n) => n + 1)), [runtime])

  // Register external actions (onReady) THEN run init flows.
  // Generation counter protects against StrictMode double-mount:
  //   Mount1: nextGeneration() → onReady → runInitFlows → unmount → nextGeneration() (invalidates)
  //   Mount2: nextGeneration() → onReady → runInitFlows (this one sticks)
  // Skip runInitFlows if blocks are empty — wait for addBlocks to bring the first batch.
  useEffect(() => {
    runtime.nextGeneration()
    onReady?.({ stateManager: runtime.stateManager, actionEngine: runtime.actionEngine })
    if (Object.keys(blocks).length > 0) {
      initFlowsRanRef.current = true
      runtime.runInitFlows()
    }

    return () => {
      // StrictMode unmount: increment generation to invalidate stale async results
      runtime.nextGeneration()
    }
  }, [runtime])

  // True unmount — destroy runtime
  useEffect(() => () => {
    if (runtimeRef.current) {
      runtimeRef.current.destroy()
      runtimeRef.current = null
    }
  }, [])

  // RenderContext for child renderers
  const ctx = useMemo<RenderContextValue>(
    () => ({
      registry: runtime.registry,
      resolveSymbol: runtime.resolveSymbol.bind(runtime),
      dispatchEvent: runtime.dispatchEvent.bind(runtime),
      navigate: runtime.navigate.bind(runtime),
      mode: "preview",
      blocks: blocks as unknown as Record<string, CogoBlockDef>,
    }),
    [runtime, blocks, tick],
  )

  // Build the page element tree (content-body injection done in runtime)
  // Placeholder blocks (type:'placeholder') are rendered through the same pipeline.
  const elements = useMemo(() => runtime.buildPageTree(), [runtime, tick])

  // Diagnostic: warn when buildPageTree returns empty (blank screen indicator)
  useEffect(() => {
    if (elements.length === 0) {
      console.warn('[AppV2] buildPageTree() returned empty — no elements to render. Check route matching and block loading.')
    }
  }, [elements])

  // Global keyboard dispatcher — allows blocks to bind "document:keydown"
  // (e.g., command-bar ⌘K). Runs once per AppV2 mount.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      runtime.dispatchEvent('document', 'keydown', {
        key: e.key,
        code: e.code,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
      })
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [runtime])

  // Isolated app style — inject theme token CSS variables for per-app theming.
  // Uses getIsolatedAppStyle() which handles both oklch and legacy HSL formats,
  // and sets --color-* alongside --* so Tailwind utilities resolve correctly.
  // When a ResolvedTheme is provided, its cssVars are merged on top so the new
  // design-system path wins over legacy themeTokens when both are present.
  const tokenStyle = useMemo<React.CSSProperties>(() => {
    const style = getIsolatedAppStyle(themeTokens ?? undefined, 'light')
    // Merge ResolvedTheme cssVars on top (new path wins over legacy themeTokens)
    if (theme?.cssVars) {
      Object.assign(style, theme.cssVars)
    }
    // Background/foreground via CSS variables (no hsl wrapper — oklch is a complete color)
    style.backgroundColor = 'var(--color-background)'
    style.color = 'var(--color-foreground)'
    // Phase 393: consume --font-sans so the wrapper div (and descendants that
    // inherit font-family) actually pick up the preset font. Without this
    // line, setting --font-sans on the wrapper has no visual effect because
    // nothing on the wrapper reads the variable. Parity with v1 ThemedWrapper.
    ;(style as Record<string, string>).fontFamily = 'var(--font-sans)'
    return style as unknown as React.CSSProperties
  }, [themeTokens, theme])

  // When running inside an iframe, also inject theme tokens into :root
  // so body/html background and all non-descendant elements get the app theme.
  // Uses the same getIsolatedAppStyle() as the inline style — single source of truth.
  useEffect(() => {
    if (window.self === window.top) return // only in iframe
    if (!themeTokens?.colors) return
    const vars = getIsolatedAppStyle(themeTokens ?? undefined, 'light')
    const root = document.documentElement
    for (const [key, value] of Object.entries(vars)) {
      if (key.startsWith('--')) root.style.setProperty(key, value)
    }
    document.body.style.backgroundColor = 'var(--color-background)'
    document.body.style.color = 'var(--color-foreground)'
    root.style.colorScheme = vars.colorScheme ?? 'light'
  }, [themeTokens])

  // CRITICAL FIX — replicate the `style-{name}` class onto <html> and <body>
  // so shadcn-preset CSS scoped under `.style-luma .cn-*` reaches Base UI
  // Portal'd content (Popover / Select / Calendar / Dialog / Tooltip / etc.).
  // Portals mount to document.body which is NOT a descendant of the AppV2
  // wrapper div — without this, every popover surface renders fully
  // transparent because none of the .style-{name} .cn-* selectors match.
  useEffect(() => {
    const styleClass = `style-${manifest.style ?? 'luma'}`
    const root = document.documentElement
    const body = document.body
    root.classList.add(styleClass)
    body.classList.add(styleClass)
    return () => {
      root.classList.remove(styleClass)
      body.classList.remove(styleClass)
    }
  }, [manifest.style])


  // Agent-chat dock → shell split view (sipass 4729aba pattern).
  // When the agent-chat block wants to dock (isOpen && isExpanded),
  // the shell switches to `flex` so the page content stays at flex-1
  // and an aria-hidden placeholder sibling reserves the right column.
  // The chat panel itself is still position: fixed so it visually
  // docks to the viewport edge, but the placeholder keeps the flex
  // layout honest — admin content reflows left instead of sliding
  // under the panel. Reading via runtime.resolveSymbol — any app
  // that includes the agent-chat block exposes these symbols; apps
  // without it get `""` (falsy) and behave normally.
  const chatIsOpen = Boolean(runtime.resolveSymbol('#{agentChat.isOpen}'))
  const chatIsExpanded = Boolean(runtime.resolveSymbol('#{agentChat.isExpanded}'))
  const reserveChatColumn = chatIsOpen && chatIsExpanded
  void tick // re-evaluate on every state change via the existing subscribe

  return (
    <AtomDefaultsProvider value={theme?.atomProps ?? {}}>
      <div data-testid="app-v2-theme-wrapper" style={{ display: 'contents' }}>
        <BreakpointProvider containerRef={appRootRef}>
          <div
            ref={appRootRef}
            data-testid="block-theme-wrapper"
            className={`style-${manifest.style ?? 'luma'} bg-background text-foreground min-h-screen ${reserveChatColumn ? 'flex' : ''}`}
            style={tokenStyle}
          >
            <div className={reserveChatColumn ? 'flex-1 min-w-0 flex flex-col' : 'contents'}>
              <RenderContext.Provider value={ctx}>
                <LayoutWrapper layout={(pageSpec?.layout ?? "single") as Layout}>
                  {elements.map((el, i) => {
                    const Renderer = runtime.registry.get(el.type)
                    if (!Renderer) return null
                    // A3.4 — derive block id from element id for click-time block identification.
                    // buildPageTree wraps stack-layout blocks in section-<blockName> wrappers;
                    // unwrapped layouts use the block name directly as the element id.
                    const rawId = typeof el.id === 'string' ? el.id : `block-${i}`
                    const blockId = rawId.startsWith('section-') ? rawId.slice('section-'.length) : rawId
                    return (
                      <div key={el.id || i} data-cogo-block-id={blockId} style={{ display: 'contents' }}>
                        <Renderer element={el as never} />
                      </div>
                    )
                  })}
                </LayoutWrapper>
              </RenderContext.Provider>
            </div>
            {reserveChatColumn && (
              // Placeholder column — mirrors the agent-chat panel's docked
              // width on sm+. Hidden below sm since the panel falls back
              // to a bottom-sheet there.
              <div
                aria-hidden="true"
                className="hidden sm:block shrink-0 w-[400px] md:w-[440px] lg:w-[480px]"
              />
            )}
            <CogoAgent
              appName={manifest.name}
              apiKey={(import.meta as unknown as { env?: Record<string, string> }).env?.VITE_GEMINI_API_KEY ?? (window as any).__GEMINI_API_KEY__}
              enabled={(manifest as any).agent?.enabled !== false}
            />
          </div>
        </BreakpointProvider>
      </div>
    </AtomDefaultsProvider>
  )
}
